import { CalendarIcon, CheckIcon, FileTextIcon, SearchIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSelfContracts } from "@/hooks/useSelfContracts";
import { cn } from "@/lib/utils";
import type { SelfContract } from "@/types/proto/api/v1/memo_service_pb";
import { useTranslate } from "@/utils/i18n";

interface ContractSearchProps {
  preferredTicker?: string;
  onSelectContract: (contract: SelfContract) => void;
  onClose: () => void;
  className?: string;
}

const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-green-300 dark:border-green-700",
  triggered: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 border-blue-300 dark:border-blue-700",
  cancelled: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-300 dark:border-gray-600",
  expired: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300 border-orange-300 dark:border-orange-700",
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
  } catch {
    return dateString;
  }
};

/**
 * ContractSearch - Manual Self-Contract search component
 *
 * Allows users to search and filter Self-Contracts by ticker, status, or template.
 */
export const ContractSearch: React.FC<ContractSearchProps> = ({ preferredTicker = "", onSelectContract, onClose, className }) => {
  const t = useTranslate();

  // Dynamic labels
  const CONTRACT_STATUS_LABELS: Record<string, string> = {
    active: t("editor.active" as any),
    triggered: t("editor.triggered" as any),
    cancelled: t("editor.cancelled" as any),
    expired: t("editor.expired" as any),
  };

  const CONTRACT_TEMPLATE_LABELS: Record<string, string> = {
    "value-oriented": t("editor.selfContractDetail.valueOriented" as any),
    "trading-oriented": t("editor.selfContractDetail.tradingOriented" as any),
  };

  const STATUS_OPTIONS = [
    { value: "all", label: t("editor.contract.allStatus" as any) },
    { value: "active", label: CONTRACT_STATUS_LABELS.active },
    { value: "triggered", label: CONTRACT_STATUS_LABELS.triggered },
    { value: "cancelled", label: CONTRACT_STATUS_LABELS.cancelled },
    { value: "expired", label: CONTRACT_STATUS_LABELS.expired },
  ];

  const TEMPLATE_OPTIONS = [
    { value: "all", label: t("editor.contract.allTypes" as any) },
    { value: "value-oriented", label: CONTRACT_TEMPLATE_LABELS["value-oriented"] },
    { value: "trading-oriented", label: CONTRACT_TEMPLATE_LABELS["trading-oriented"] },
  ];

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [templateFilter, setTemplateFilter] = useState<string>("all");

  // Fetch contracts with current filters
  const {
    data: contractsData,
    isLoading,
    isError,
  } = useSelfContracts({
    ticker: preferredTicker || undefined,
    status: statusFilter !== "all" ? statusFilter : undefined,
    template: templateFilter !== "all" ? templateFilter : undefined,
    pageSize: 50,
  });

  const contracts = contractsData?.contracts || [];

  function filtersActive(status: string, template: string): boolean {
    return status !== "all" || template !== "all";
  }

  const handleClearFilters = () => {
    setStatusFilter("all");
    setTemplateFilter("all");
  };

  return (
    <div className={cn("rounded-lg border bg-card shadow-sm", className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <SearchIcon className="w-4 h-4 text-primary" />
          <span className="font-medium text-sm">搜索合约</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title="关闭搜索"
        >
          <XIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Filters */}
      <div className="p-4 space-y-3 border-b">
        {/* Status and Template filters */}
        <div className="flex gap-2">
          <div className="flex-1">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="选择状态" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Select value={templateFilter} onValueChange={setTemplateFilter}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="选择类型" />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Clear filters button */}
        {filtersActive(statusFilter, templateFilter) && (
          <Button type="button" variant="ghost" size="sm" onClick={handleClearFilters} className="h-8 px-2 text-xs">
            清除筛选条件
          </Button>
        )}
      </div>

      {/* Results */}
      <div className="max-h-80 overflow-y-auto p-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">{t("editor.contract.loading" as any)}</div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-8 text-sm text-muted-foreground">
            {t("editor.contract.loadFailed" as any)}
          </div>
        ) : contracts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-sm text-muted-foreground">
            {t("editor.contract.notFound" as any)}
          </div>
        ) : (
          <div className="space-y-2">
            {contracts.map((contract) => (
              <ContractListItem
                key={contract.contractId}
                contract={contract}
                statusLabels={CONTRACT_STATUS_LABELS}
                templateLabels={CONTRACT_TEMPLATE_LABELS}
                t={t}
                onSelect={() => onSelectContract(contract)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface ContractListItemProps {
  contract: SelfContract;
  statusLabels: Record<string, string>;
  templateLabels: Record<string, string>;
  t: (key: any) => string;
  onSelect: () => void;
}

const ContractListItem: React.FC<ContractListItemProps> = ({ contract, statusLabels, templateLabels, t, onSelect }) => {
  const statusColor = statusColors[contract.status] || statusColors.active;

  // Get trigger summary
  const getTriggerSummary = (): string => {
    const trigger = contract.trigger;
    if (!trigger) return t("editor.selfContractDetail.notSet" as any);
    switch (trigger.type) {
      case "price": {
        const op = trigger.operator || "==";
        const price = trigger.priceLevel;
        return price !== undefined
          ? `${t("editor.selfContractDetail.price" as any)} ${op} ${price.toFixed(2)}`
          : t("editor.selfContractDetail.priceTrigger" as any);
      }
      case "expiration":
        return `${t("editor.contract.expires" as any)}: ${formatDate(contract.expiration)}`;
      case "time":
        return trigger.notes || t("editor.selfContractDetail.timeTrigger" as any);
      case "custom":
        return trigger.notes || t("editor.selfContractDetail.custom" as any);
      default:
        return t("editor.selfContractDetail.notSet" as any);
    }
  };

  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full flex items-start gap-3 p-3 rounded-md border hover:border-primary hover:bg-accent/50 transition-all text-left group"
    >
      {/* Left column: Contract info */}
      <div className="flex-1 min-w-0">
        {/* Header: Ticker + Template + Status */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Badge variant="outline" className="font-bold">
            {contract.ticker}
          </Badge>
          <Badge variant="secondary" className="text-xs">
            {templateLabels[contract.template] || contract.template}
          </Badge>
          <Badge className={cn("text-xs", statusColor)}>{statusLabels[contract.status] || contract.status}</Badge>
        </div>

        {/* Details */}
        <div className="space-y-1 mb-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <CalendarIcon className="w-3 h-3" />
            <span>
              {t("editor.contract.expires" as any)}: {formatDate(contract.expiration)}
            </span>
          </div>
          {contract.trigger && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <FileTextIcon className="w-3 h-3" />
              <span>
                {t("editor.selfContractDetail.triggerCondition" as any)}: {getTriggerSummary()}
              </span>
            </div>
          )}
        </div>

        {/* Idea */}
        {contract.idea && <p className="text-sm text-muted-foreground line-clamp-2">{contract.idea}</p>}
      </div>

      {/* Select indicator */}
      <div className="flex items-center justify-center w-8 h-8 rounded-full border border-primary/30 bg-primary/5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <CheckIcon className="w-4 h-4 text-primary" />
      </div>
    </button>
  );
};

export default ContractSearch;
